import React, { useState } from 'react';
import BudgetCard from 'routes/BudgetModule/components/BudgetProposal/shared/Card/BudgetCard';
import { BudgetExpenseOrIncome } from 'business/constants';
import { useTranslation } from 'react-i18next';
import { translateBlock, translateBlockValue } from 'i18n';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import ExportMenuContainer from 'components/ExportMenu/ExportMenuContainer';
import {
    ANALYSE_PIVOT_TABLE_TO_HIDE_BY_DEFAULT,
    dimToPivotInRequest,
} from 'routes/BudgetModule/config/constants';
import { PivotFieldsAggridT, ChartSettingsT } from 'business/definitions';
import { BudgetProposalT } from 'routes/BudgetModule/config/types';
import { getChartSettings } from 'routes/BudgetModule/helpers/chartHelpers';
import { getPivotQuerySettings } from 'routes/BudgetModule/config/queries';
import { useMutateBudgetProposalSettings } from 'routes/BudgetModule/components/BudgetProposal/Resource/ExpenseIncome/hooks/budgetProposalSettings';
import _ from 'lodash';
import AnalysePivotGrid from './AnalysePivotGrid';
import { SelectedChartForArgumentT } from '../Analyse';
import { getPivotGridMutationBody } from '../helpers/pivotGrid';
import { useGridFiltersHelper } from '../hooks/useGridFiltersHelper';

const StyAnalyseCharts = styled(Box, {
    name: 'StyAnalyseCharts',
})({
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    flexFlow: 'column wrap',
    width: '100%',
});

const StyBudgetCardContainer = styled(Box, {
    name: 'StyBudgetCardContainer',
})({
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    width: '100%',
});

type Props = {
    expenseOrIncome: BudgetExpenseOrIncome;
    exercice: number;
    resourceName: string;
    budgetProposal: BudgetProposalT;
    resourceType: string;
    softwareId: string | null;
    pivotTableQuery: QueryT;
    query: QueryT;
    onAddToArgumentModalOpen: (
        selectedChart: SelectedChartForArgumentT,
        indicatorName: string,
    ) => void;
    pivotTableSettings: PivotFieldsAggridT | undefined;
    isLoading: boolean;
};

const AnalyseCharts = ({
    expenseOrIncome,
    exercice,
    resourceName,
    budgetProposal,
    query,
    resourceType,
    softwareId,
    pivotTableQuery,
    onAddToArgumentModalOpen,
    pivotTableSettings,
    isLoading,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const { gridFilters, updateAnalyseGridFilters: updateGridFilters } =
        useGridFiltersHelper(exercice);

    const [exporterToExcel, setExporterToExcel] = useState<null | (() => void)>(
        null,
    );

    const title = t('budgetProposal.analyseTitle', {
        expenseOrIncome: translateBlockValue(expenseOrIncome, true),
        exercice,
        orgChart: resourceName,
    });
    const saveButtonTitle = t('budgetProposal.analyseSaveButtonTitle');
    const pivotTableTitle = t('budgetProposal.pivotTableYearlyComparison', {
        expenseOrIncome: translateBlockValue(expenseOrIncome, true),
        dimensions: translateBlock(budgetProposal.by, true),
    });
    const pivotTableFieldsSettings = getPivotQuerySettings(
        pivotTableQuery,
        [dimToPivotInRequest],
        ANALYSE_PIVOT_TABLE_TO_HIDE_BY_DEFAULT,
    );
    const getPivotTableSettings = (): ChartSettingsT => {
        // don't remove this cloneDeep, else we modify global variables from getChartSettings
        const settings = _.cloneDeep(getChartSettings('pivot_table'));
        delete settings.legendPosition;
        settings.pivotFields = pivotTableFieldsSettings;
        settings.pivotFieldsAggrid = pivotTableSettings;
        settings.gridFilters = gridFilters;

        return settings;
    };

    const pivotTableSettingsQuerySpecific = getPivotTableSettings();

    const addToArgument = () =>
        onAddToArgumentModalOpen(
            {
                query,
                title: pivotTableTitle,
                settings: pivotTableSettingsQuerySpecific,
            },
            'budget_analysis',
        );

    const { mutate: mutateAnalyseSettings } = useMutateBudgetProposalSettings(
        budgetProposal,
        resourceType,
        softwareId,
    );

    const updateAnalyseSettings = (pivotFieldsAggrid: PivotFieldsAggridT) => {
        const body = getPivotGridMutationBody(
            expenseOrIncome,
            pivotFieldsAggrid,
        );
        mutateAnalyseSettings(body);
    };

    return (
        <StyAnalyseCharts component="section" key={expenseOrIncome}>
            <Typography
                sx={{ textAlign: 'center', margin: '1rem' }}
                variant="h4"
                color="primary"
            >
                {title}
            </Typography>

            <StyBudgetCardContainer>
                <BudgetCard
                    title={pivotTableTitle}
                    saveButtonTitle={saveButtonTitle}
                    exportMenu={
                        <ExportMenuContainer
                            exportExcelData={exporterToExcel}
                        />
                    }
                    sx={{ width: '100%', margin: '1%' }}
                    onSubmit={addToArgument}
                >
                    <AnalysePivotGrid
                        filters={query.filters}
                        onSettingsUpdate={updateAnalyseSettings}
                        updateGridFilters={updateGridFilters}
                        gridFilters={gridFilters}
                        selectedYear={exercice}
                        budgetProposal={budgetProposal}
                        resourceName={resourceName}
                        setExporterToExcel={setExporterToExcel}
                        settings={pivotTableSettings}
                        isLoading={isLoading}
                    />
                </BudgetCard>
            </StyBudgetCardContainer>
        </StyAnalyseCharts>
    );
};

export default AnalyseCharts;
